/**
 * useTourVoice — narrates tour scenes using OpenAI TTS (nova voice) via the
 * backend `/api/tours/tts` endpoint.
 *
 * Why server-side TTS instead of Web Speech API?
 *   • Native browser voices are stiff and synthetic. OpenAI's `nova` voice
 *     is warm, female, American — far closer to a documentary narrator.
 *   • Backend caches the audio bytes per (text+voice+model+speed) hash, so
 *     replaying a tour doesn't re-charge the universal LLM key.
 *
 * Behavior
 *   • On every scene change, the caller invokes `speak(text, { onEnd })`.
 *     The hook fetches the audio (HEAD → MP3), plays it via HTMLAudioElement,
 *     and fires `onEnd` when playback ends so the engine can auto-advance.
 *   • Estimates the spoken character index over time (so the narrative box
 *     can scroll in rough lockstep with the audio — OpenAI TTS does not
 *     expose word-level timing, so we approximate via elapsed/duration).
 *   • `cancel()` aborts both any in-flight fetch and the audio playback.
 *   • Muted? speak() invokes onEnd immediately with `{ skipped: true }` so
 *     the engine falls back to a timer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const LS_MUTED = 'district_tour_voice_muted';
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useTourVoice() {
  const supported = typeof window !== 'undefined' && typeof Audio !== 'undefined';

  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(LS_MUTED) === '1'; } catch { return false; }
  });
  const [speaking, setSpeaking] = useState(false);
  // Live char-offset within the current utterance (approximated from
  // elapsed/duration since OpenAI TTS doesn't expose word boundaries).
  // Drives the auto-scrolling narrative box in the floating panel.
  const [charIndex, setCharIndex] = useState(0);
  const [scriptLength, setScriptLength] = useState(0);

  const audioRef = useRef(null);
  const abortRef = useRef(null);
  const endCbRef = useRef(null);
  const tickRef  = useRef(null);
  const objUrlRef = useRef(null);
  // Prefetch cache — maps text → { blob, promise? }. The tour engine warms
  // the *next* beat's TTS blob while the current one is speaking so
  // sentence-to-sentence transitions feel like continuous narration instead
  // of "sentence → 800 ms of dead air while we round-trip OpenAI → sentence."
  // Bounded to 24 entries (~1 tour worth of beats); LRU-ish eviction by
  // insertion order so long sessions never leak.
  const prefetchRef = useRef(new Map());
  const PREFETCH_LIMIT = 24;

  const teardownAudio = useCallback(() => {
    if (tickRef.current)  { clearInterval(tickRef.current); tickRef.current = null; }
    if (audioRef.current) {
      // Detach event handlers BEFORE pause/src='' so the synchronous
      // side-effects of teardown don't fire stale onended/onerror handlers
      // (which would mistakenly invoke the next scene's onEnd callback and
      // cause the tour to fast-forward through multiple scenes at once).
      try { audioRef.current.onended      = null; } catch (_) { /* */ }
      try { audioRef.current.onerror      = null; } catch (_) { /* */ }
      try { audioRef.current.ontimeupdate = null; } catch (_) { /* */ }
      try { audioRef.current.pause(); } catch (_) { /* */ }
      try { audioRef.current.src = ''; } catch (_) { /* */ }
      // Detach from DOM (we attached it in speak() for autoplay-policy safety).
      try {
        if (audioRef.current.parentNode) audioRef.current.parentNode.removeChild(audioRef.current);
      } catch (_) { /* */ }
      audioRef.current = null;
    }
    if (objUrlRef.current) {
      try { URL.revokeObjectURL(objUrlRef.current); } catch (_) { /* */ }
      objUrlRef.current = null;
    }
  }, []);

  // prefetch(text) — kick off (or return an in-flight) TTS fetch for `text`
  // and cache the resulting audio blob so a subsequent `speak(text)` skips
  // the network entirely. Silent, best-effort, safe to call any number of
  // times. Returns a promise that resolves when the blob is ready.
  const prefetch = useCallback(async (text) => {
    if (!supported || muted || !text) return null;
    const cache = prefetchRef.current;
    const hit = cache.get(text);
    if (hit) {
      // Move to end of insertion order so recently-touched entries survive
      // eviction longer.
      cache.delete(text);
      cache.set(text, hit);
      return hit.blob || hit.promise;
    }
    const promise = (async () => {
      try {
        const res = await fetch(`${API_BASE}/tours/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, voice: 'nova', model: 'tts-1-hd', speed: 0.95 }),
        });
        if (!res.ok) throw new Error(`tts prefetch http ${res.status}`);
        const blob = await res.blob();
        const entry = cache.get(text);
        if (entry) entry.blob = blob;
        return blob;
      } catch (_) {
        cache.delete(text);
        return null;
      }
    })();
    cache.set(text, { promise });
    // LRU-ish trim
    while (cache.size > PREFETCH_LIMIT) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    return promise;
  }, [supported, muted]);

  // speak(text, { onEnd }) — fetches mp3 from backend and plays it.
  // If muted/unsupported, invokes onEnd({ skipped: true }) immediately so
  // the caller can fall back to a timer.
  const speak = useCallback(async (text, opts = {}) => {
    const { onEnd } = opts;
    endCbRef.current = onEnd || null;
    if (!supported || muted || !text) {
      if (onEnd) onEnd({ skipped: true });
      return;
    }
    // Cancel any in-flight playback / fetch before starting a new one.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* */ }
    }
    teardownAudio();

    const controller = new AbortController();
    abortRef.current = controller;
    setSpeaking(true);
    setCharIndex(0);
    setScriptLength(text.length);

    let audioBlob;
    // Fast path: prefetched blob already sitting in the cache. Skips the
    // TTS round-trip entirely — sentence-to-sentence transitions inside a
    // scene stay continuous.
    const cached = prefetchRef.current.get(text);
    if (cached?.blob) {
      audioBlob = cached.blob;
      // Consume so we don't accumulate stale entries forever.
      prefetchRef.current.delete(text);
    } else if (cached?.promise) {
      // A prefetch is in-flight — await it rather than starting a duplicate
      // request. On failure we fall through to the direct fetch below.
      try {
        const blob = await cached.promise;
        if (blob) {
          audioBlob = blob;
          prefetchRef.current.delete(text);
        }
      } catch (_) { /* fall through */ }
    }
    if (!audioBlob) {
      try {
        const res = await fetch(`${API_BASE}/tours/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, voice: 'nova', model: 'tts-1-hd', speed: 0.95 }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`tts http ${res.status}`);
        audioBlob = await res.blob();
      } catch (e) {
        if (e.name === 'AbortError') return; // expected on scene change
        console.warn('[tour-voice] tts fetch failed', e);
        setSpeaking(false);
        const cb = endCbRef.current;
        endCbRef.current = null;
        if (cb) cb({ skipped: true, error: true });
        return;
      }
    }
    if (controller.signal.aborted) return;

    const url = URL.createObjectURL(audioBlob);
    objUrlRef.current = url;
    const a = new Audio(url);
    a.preload = 'auto';
    // Append to DOM. Chromium-based browsers (Chrome, Edge, Brave) treat
    // detached HTMLAudioElements as "non-interactive" under the autoplay
    // policy even when a recent user gesture exists — `play()` returns a
    // rejected Promise mid-tour ("audio narration doesn't start after Next")
    // unless the element is actually in the document. Hidden + zero-sized so
    // it never affects layout; cleaned up by teardownAudio() on scene change.
    try {
      a.style.position = 'fixed';
      a.style.left = '-9999px';
      a.style.width = '1px';
      a.style.height = '1px';
      a.setAttribute('data-tour-voice', '1');
      document.body.appendChild(a);
    } catch (_) { /* ignore */ }
    audioRef.current = a;

    const finish = (errored = false) => {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      setSpeaking(false);
      setCharIndex(text.length);
      const cb = endCbRef.current;
      endCbRef.current = null;
      // When playback errored (autoplay blocked, network error, etc.) we MUST
      // signal `skipped: true` so the tour engine falls back to its
      // autoAdvanceMs timer instead of treating it as a successfully-ended
      // narration and rushing to the next scene. Without this, errored
      // playbacks cascade-advance through the whole tour in a few seconds.
      if (cb) cb({ skipped: errored, error: errored });
    };

    a.onended = () => finish(false);
    a.onerror = () => finish(true);

    // Approximate char-index from audio playback time so the narrative
    // scroller in the floating panel can follow the voice in lockstep.
    a.ontimeupdate = () => {
      if (!a.duration || !isFinite(a.duration)) return;
      const ratio = Math.max(0, Math.min(1, a.currentTime / a.duration));
      setCharIndex(Math.round(text.length * ratio));
    };

    try {
      // Some browsers reject .play() without a user gesture — fall back to
      // calling onEnd so the engine doesn't stall.
      const p = a.play();
      if (p && typeof p.then === 'function') {
        p.catch((e) => {
          console.warn('[tour-voice] play rejected', e);
          finish(true);
        });
      }
    } catch (e) {
      console.warn('[tour-voice] play threw', e);
      finish(true);
    }
  }, [supported, muted, teardownAudio]);

  const cancel = useCallback(() => {
    endCbRef.current = null; // suppress any pending onEnd notification
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* */ }
      abortRef.current = null;
    }
    teardownAudio();
    setSpeaking(false);
  }, [teardownAudio]);

  const toggleMute = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      try { localStorage.setItem(LS_MUTED, next ? '1' : '0'); } catch (_) { /* */ }
      if (next) {
        if (abortRef.current) { try { abortRef.current.abort(); } catch (_) { /* */ } }
        teardownAudio();
      }
      return next;
    });
  }, [teardownAudio]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (abortRef.current) { try { abortRef.current.abort(); } catch (_) { /* */ } }
    teardownAudio();
  }, [teardownAudio]);

  return {
    supported,
    muted,
    speaking,
    charIndex,
    scriptLength,
    voices: [],         // legacy compatibility (voice is server-driven now)
    voiceName: 'OpenAI nova',
    speak,
    prefetch,
    cancel,
    toggleMute,
    setVoice: () => {}, // legacy no-op
  };
}
